import { useContext, useEffect, useState } from "react";
import Layout from "../../components/layout/Layout";
import { useSyncSelectedClient } from "../../Hooks/useSyncSelected";
import { AuthContext } from "../../state/AuthContext";
import { ClientContext } from "../../state/ClientContext";
import "./ClientProfilePage.css";
import { toTitleCase } from "../../util/toTitleCase";
import ClientSignInInfo from "./ClientSignInInfo";
import ClientReferences from "./ClientReferences";
import ClientDetailsForm from "./ClientDetailsForm";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmationModal";
import ForceLogoutModal from "../../components/modals/ForceLogoutModal";
import ForceLogoutButton from "../../components/ForceLogoutButton";
import ForceLogoutSuccessBanner from "../../components/ForceLogoutSuccessBanner";
import DeviceBuildChips from "../../components/DeviceBuildChips";
import { useForceLogout } from "../../Hooks/useForceLogout";
import { useUserPresence } from "../../Hooks/useFirebasePresence";
import XandarKardianSection from "./XandarKardianSection";
import WatchVitalsSection from "./WatchVitalsSection";
import WeatherHazardsSection from "./WeatherHazardsSection";
import DeviceLocationSection from "./DeviceLocationSection";
import PontosenseStaffSection from "../../components/pontosense/PontosenseStaffSection";
import IotEventHistorySection from "../../components/iot/IotEventHistorySection";
import NoraChatPanel from "../../components/nora/NoraChatPanel";
import useClientWeather from "../../Hooks/useClientWeather";
// GMA-1150 caregiver-access stub not mounted on client profile (NA-1451).
// Re-enable via PontosenseCaregiverAccessSection when matrix is re-scoped.
// Docs: CompassCare-Documentation/PONTOSENSE_CAREGIVER_ACCESS_MODEL.md
import {
  canResetClientPassword,
  canShowPontosenseStaffPane,
  canShowXandarStaffPane,
  canShowWatchStaffPane,
  isCaregiverAdminOrSuper,
  canShowDeviceBuildChips,
} from "../../utils/clientProfileAccess";
import { IOT_VENDORS } from "../../constants/iotVendors";
import { fetchWatchIntegrationEnabled } from "../../api/watchService";
import PanelErrorBoundary from "../../components/errorBoundary/PanelErrorBoundary";

const ClientProfilePage = () => {
  const { selectedClient, deleteClient, isDeletingClient, refetchClients } =
    useContext(ClientContext);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const weather = useClientWeather(selectedClient?.id);
  useSyncSelectedClient();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // GMA-1865: caregivers must not be gated solely on roster appleWatchEnabled —
  // resolve authoritative status above showWatchPane so a stale-false roster
  // flag cannot hide an enabled Watch pane.
  // #624 review round 3: store the resolved flag WITH the client it answers
  // for. This page is not remounted when `selectedClient` changes (same reason
  // NoraChatPanel resets on client change — GMA-1573), so a bare boolean is
  // still client A's answer during the first render for client B.
  const [caregiverWatch, setCaregiverWatch] = useState(null);
  const caregiverWatchEnabled =
    caregiverWatch && caregiverWatch.clientId === selectedClient?.id
      ? caregiverWatch.enabled
      : null;

  const forceLogout = useForceLogout(selectedClient?.id);
  // #489: the presence beat already carries app version + build number per
  // device; QA reads them off this page instead of production journald.
  const { devicesMap } = useUserPresence(selectedClient?.id) || {};
  const isAdminOrSuper = isCaregiverAdminOrSuper(currentUser);
  // cortex-be#1501 / GMA-1402: default OFF — only an explicit `true` is on.
  const pontosenseEnabled = selectedClient?.pontosenseIntegrationEnabled === true;
  // Whether the client actually HAS a sensor, which the enabled flag cannot say:
  // the flag answers "is this permitted", and existing clients created before
  // #1501 still carry the old default of true. `iotVendors` counts inactive device rows too, because
  // unbinding is soft and a device swap must not blank the pane mid-care.
  const clientIotVendors = selectedClient?.iotVendors ?? [];
  const pontosenseHasDevice = clientIotVendors.includes(IOT_VENDORS.PONTOSENSE);
  const xandarEnabled = selectedClient?.xandarKardianEnabled === true;
  const showPontosensePane = canShowPontosenseStaffPane(currentUser, {
    integrationEnabled: pontosenseEnabled,
    hasDevice: pontosenseHasDevice,
  });
  const showXandarPane = canShowXandarStaffPane(currentUser, {
    integrationEnabled: xandarEnabled,
  });
  const watchEnabledRoster = selectedClient?.appleWatchEnabled === true;
  const watchEnabledForGate = isAdminOrSuper
    ? watchEnabledRoster
    : caregiverWatchEnabled === null
      ? watchEnabledRoster
      : caregiverWatchEnabled === true;
  const showWatchPane = canShowWatchStaffPane(currentUser, {
    integrationEnabled: watchEnabledForGate,
  });
  // IoT Alert History spans every vendor (one `iot_events` table), so it is NOT
  // gated on the union of the vendor panes — that OR had to be widened per
  // vendor and could never be satisfied by boundarycare/hydrostasis, which have
  // no pane. Four invariants, each load-bearing (drop one and a test fails):
  //   role      — outer conjunct; every `canShow*` opens with the same check, so
  //               omitting it would leak sensor PRESENCE to any other role.
  //   admin     — `iotVendors` is caregiver-payload only, so a device test alone
  //               hides the section from every admin.
  //   device    — covers the four vendors that have a row, inactive included.
  //   watch     — a flag with NO device row, so `iotVendors` cannot see it; reads
  //               `watchEnabledForGate`, not `showWatchPane`, which is true for
  //               any admin and would leave the admin term dead.
  // Rationale and receipts: #510, #525, #526.
  const isCareStaff = currentUser?.isCaregiver === true || isAdminOrSuper;
  const showIotAlertHistory =
    isCareStaff &&
    (isAdminOrSuper ||
      clientIotVendors.length > 0 ||
      watchEnabledForGate === true);
  const showPasswordReset = canResetClientPassword(currentUser);

  useEffect(() => {
    if (!selectedClient?.id || isAdminOrSuper) {
      setCaregiverWatch(null);
      return undefined;
    }
    const forClient = selectedClient.id;
    let cancelled = false;
    const load = () => {
      fetchWatchIntegrationEnabled(selectedClient.id)
        .then((on) => {
          if (!cancelled) setCaregiverWatch({ clientId: forClient, enabled: Boolean(on) });
        })
        .catch(() => {
          if (!cancelled)
            setCaregiverWatch({ clientId: forClient, enabled: watchEnabledRoster });
        });
    };
    // Re-poll so an admin toggle is reflected for caregivers even when the
    // roster never flips appleWatchEnabled (authoritative GET is source of truth).
    load();
    const id = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [selectedClient?.id, isAdminOrSuper, watchEnabledRoster]);

  const handleDelete = async () => {
    if (!selectedClient?.id) return;

    try {
      await deleteClient(selectedClient.id);
      setShowDeleteModal(false);
      navigate("/select-client-homepage");
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  if (!selectedClient) {
    return null;
  }

  const clientName = toTitleCase(selectedClient.name);

  return (
    <Layout containerClassName="bg-page-light">
      <h1
        style={{
          textAlign: "left",
          fontWeight: "800",
          marginLeft: "40px",
          paddingTop: "20px",
        }}
      >
        <span style={{ fontSize: "2.4rem", color: "#222" }}>{clientName}</span>
      </h1>

      {/* #489: per-device build, readable without hovering — the surface QA uses
          to tell build 117 from 118. #529: admin/super only. The row renders one
          chip per device IDENTITY, and a stale identity outlives the device by up
          to cortex-be's 30-day prune, so a caregiver was being shown weeks-dead
          chips where they needed "is this device working now?". */}
      {canShowDeviceBuildChips(currentUser) && (
        <div style={{ marginLeft: "40px", marginBottom: "8px" }}>
          <PanelErrorBoundary key={`peb-${selectedClient.id}-9`} panelName="Device build">
            <DeviceBuildChips devicesMap={devicesMap} />
          </PanelErrorBoundary>
        </div>
      )}

      {/* Force Logout Success/Info Banner */}
      <PanelErrorBoundary key={`peb-${selectedClient.id}-10`} panelName="Sign-out banner">
        <ForceLogoutSuccessBanner
          result={forceLogout.result}
          clientName={selectedClient.name}
        />
      </PanelErrorBoundary>

      {/* Stable hooks for the placement guard: the columns invariant is
          "history shares the left column with the details form", and an
          inline-style selector pins the CSS spelling rather than that
          invariant (style serialization varies by React/JSDOM version). */}
      <div
        data-testid="client-profile-columns"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          padding: "16px",
        }}
      >
        <div
          data-testid="client-profile-left-column"
          style={{ flex: "1 1 480px", minWidth: 0 }}
        >
          <PanelErrorBoundary key={`peb-${selectedClient.id}-11`} panelName="Client details">
            <ClientDetailsForm />
          </PanelErrorBoundary>
          {isAdminOrSuper && (
            <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
              <button
                className={`delete-client btn-danger`}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete client
              </button>
              {currentUser?.isAdmin && (
                <PanelErrorBoundary key={`peb-${selectedClient.id}-12`} panelName="Sign-out control">
                  <ForceLogoutButton
                    onClick={forceLogout.openModal}
                    disabled={forceLogout.isLoading}
                  />
                </PanelErrorBoundary>
              )}
            </div>
          )}
          {/* GMA-1400: vendor-agnostic IoT alert history + audit export.
              Placed in the LEFT column, under the details form, because it is
              the tallest element on the page — at the foot of the right column
              it pushed Weather / Nora / sign-in / references below the fold
              while this space sat empty. Visibility is `showIotAlertHistory`:
              care staff only; admins always; a caregiver when the client has a
              device row OR the Watch enabled — the Watch has no device row, so
              it is a separate term. The table spans all vendors and row access
              is enforced server-side. */}
          {showIotAlertHistory && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-1`} panelName="Alert history">
              <IotEventHistorySection
                key={`iot-history-${selectedClient.id}`}
                clientId={selectedClient.id}
                clientTimezone={selectedClient.timezone}
              />
            </PanelErrorBoundary>
          )}
        </div>

        <div style={{ flex: "1 1 480px", minWidth: 0 }}>
          {/* GMA-1865: Watch + Pontosense first so Caregiver Admin on/off is
              visible without scrolling past Weather/Nora. */}
          {showWatchPane && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-2`} panelName="Watch vitals">
              <WatchVitalsSection
                key={`watch-${selectedClient.id}`}
                clientId={selectedClient.id}
                clientName={selectedClient.name}
                integrationEnabled={watchEnabledForGate}
                canManageIntegration={isAdminOrSuper}
                // #624: this page already runs the authoritative GET for
                // caregivers (and re-polls it), so the section must not repeat
                // it. Exactly mirrors the effect's own `isAdminOrSuper` guard.
                parentResolvesEnabled={!isAdminOrSuper}
                authoritativeEnabled={caregiverWatchEnabled}
                onIntegrationChanged={async () => {
                  await refetchClients?.();
                }}
              />
            </PanelErrorBoundary>
          )}
          {showPontosensePane && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-3`} panelName="Pontosense">
              <PontosenseStaffSection
                key={`pontosense-${selectedClient.id}`}
                clientId={selectedClient.id}
                clientTimezone={selectedClient.timezone}
                integrationEnabled={pontosenseEnabled}
                canManageIntegration={isAdminOrSuper}
                onIntegrationChanged={async () => {
                  await refetchClients?.();
                }}
              />
            </PanelErrorBoundary>
          )}
          {showXandarPane && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-4`} panelName="Xandar">
              <XandarKardianSection
                key={`xandar-${selectedClient.id}`}
                clientId={selectedClient.id}
                clientName={selectedClient.name}
                clientTimezone={selectedClient.timezone}
                integrationEnabled={xandarEnabled}
                canManageIntegration={isAdminOrSuper}
                onIntegrationChanged={async () => {
                  await refetchClients?.();
                }}
              />
            </PanelErrorBoundary>
          )}
          <PanelErrorBoundary key={`peb-${selectedClient.id}-5`} panelName="Weather & hazards">
            <WeatherHazardsSection
              key={`weather-${selectedClient.id}`}
              loading={weather.loading}
              error={weather.error}
              data={weather.data}
              onRefresh={weather.reload}
            />
          </PanelErrorBoundary>
          {/* GMA-1778 light UI: device elevation / speed / direction cards.
              Command Center map (GMA-1779) is separate — this is Client Profile. */}
          <PanelErrorBoundary key={`peb-${selectedClient.id}-6`} panelName="Device location">
            <DeviceLocationSection
              key={`device-location-${selectedClient.id}`}
              loading={weather.loading}
              error={weather.error}
              data={weather.data}
              onRefresh={weather.reload}
            />
          </PanelErrorBoundary>
          {(currentUser?.isCaregiver ||
            currentUser?.isAdmin ||
            currentUser?.isSuperAdmin) && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-7`} panelName="Nora">
              <NoraChatPanel
                key={`nora-${selectedClient.id}`}
                clientId={selectedClient.id}
                clientName={clientName}
                caregiverName={currentUser?.name}
              />
            </PanelErrorBoundary>
          )}
          {/* GMA-1150 stub omitted — install/alerts via PontosenseStaffSection; see PONTOSENSE_CAREGIVER_ACCESS_MODEL.md */}
          {/* IoT Alert History moved to the left column (above) — it is the
              tallest section and was burying everything below it here. */}
          {showPasswordReset && (
            <PanelErrorBoundary key={`peb-${selectedClient.id}-15`} panelName="Sign-in info">
              <ClientSignInInfo />
            </PanelErrorBoundary>
          )}
          {/* GMA-1277 (ported from main 5e0c82b): explain the password-reset
              gate to standard caregivers instead of silently hiding it. */}
          {currentUser?.isCaregiver && !showPasswordReset && (
            <div
              className="client-profile-section"
              style={{
                marginBottom: "24px",
                backgroundColor: "#D9D9D9",
                color: "#333",
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <h2 style={{ fontSize: "1.1rem", marginTop: 0 }}>
                Client password (My Compass)
              </h2>
              <p style={{ marginBottom: 0, lineHeight: 1.5 }}>
                Generating a new client sign-in password is limited to{" "}
                <strong>caregiver admin</strong> or <strong>super admin</strong>{" "}
                accounts. You are signed in as a standard caregiver, so this
                control is hidden. Sign out and sign back in with a caregiver
                admin or super admin account that is allowed to reset client
                passwords, then return to this client profile and use{" "}
                <strong>Generate new Password</strong> under{" "}
                <strong>Client sign-in info</strong>.
              </p>
            </div>
          )}
          <PanelErrorBoundary key={`peb-${selectedClient.id}-8`} panelName="References">
            <ClientReferences />
          </PanelErrorBoundary>
        </div>
      </div>

      {/* Force Logout Confirmation Modal */}
      <PanelErrorBoundary key={`peb-${selectedClient.id}-13`} panelName="Sign-out dialog">
        <ForceLogoutModal
          isOpen={forceLogout.showModal}
          isLoading={forceLogout.isLoading}
          error={forceLogout.error}
          clientName={selectedClient.name}
          onConfirm={forceLogout.forceLogout}
          onCancel={forceLogout.closeModal}
        />
      </PanelErrorBoundary>

      {/* Delete Confirmation Modal */}
      <PanelErrorBoundary key={`peb-${selectedClient.id}-14`} panelName="Delete dialog">
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          isLoading={isDeletingClient}
          itemName={selectedClient.name}
          itemType="Client"
          warningMessage="All client data, activities, and records will be permanently removed."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </PanelErrorBoundary>
    </Layout>
  );
};

export default ClientProfilePage;
